import { useState, useCallback, useRef } from 'react'
import { API_BASE } from '../lib/api'
import { useStore } from '../lib/store'

async function tryRefresh(): Promise<boolean> {
  try {
    const r = await fetch(`${API_BASE}/api/auth/refresh`, {
      method: 'POST',
      credentials: 'include',  // Send the HttpOnly sb_refresh cookie
    })
    if (!r.ok) return false
    const data = await r.json()
    if (!data.access_token) return false
    useStore.getState().setAccessToken(data.access_token)
    return true
  } catch {
    return false
  }
}

export type MessageRole = 'user' | 'assistant'

export interface ChatMessage {
  id: string
  role: MessageRole
  content: string
  toolCalls?: ToolEvent[]
  isStreaming?: boolean
  timestamp: Date
}

export interface ToolEvent {
  type: 'tool_call' | 'tool_result'
  name: string
  input?: Record<string, unknown>
  result?: Record<string, unknown>
}

interface UseChatStreamOptions {
  conversationId: string
  onConfigUpdate?: (status: string) => void
}

export function useChatStream({ conversationId, onConfigUpdate }: UseChatStreamOptions) {
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [activeTools, setActiveTools] = useState<ToolEvent[]>([])
  const abortRef = useRef<AbortController | null>(null)

  // Streaming text buffer — accumulates chunks between animation frames.
  // We write here on every SSE event but only call setMessages() once per
  // requestAnimationFrame (~16 ms / 60 fps) to avoid thrashing React's
  // reconciler with hundreds of micro-renders during long responses.
  const streamTextRef = useRef('')
  const rafIdRef = useRef<number>(0)      // handle returned by requestAnimationFrame
  const rafPendingRef = useRef(false)     // true only while a RAF callback is queued

  const sendMessage = useCallback(async (
    content: string,
    options: {
      customerApiDoc?: string
      customerApiDocType?: string
      endpointsFilterData?: Record<string, any>
      schemaUserImportData?: Record<string, any>
    } = {}
  ) => {
    if (isLoading) return

    const userMsg: ChatMessage = {
      id: crypto.randomUUID(),
      role: 'user',
      content,
      timestamp: new Date()
    }
    setMessages(prev => [...prev, userMsg])
    setIsLoading(true)
    setActiveTools([])

    // Placeholder assistant message for streaming
    const assistantId = crypto.randomUUID()
    const assistantMsg: ChatMessage = {
      id: assistantId,
      role: 'assistant',
      content: '',
      toolCalls: [],
      isStreaming: true,
      timestamp: new Date()
    }
    setMessages(prev => [...prev, assistantMsg])

    abortRef.current = new AbortController()

    const doFetch = () => {
      const token = useStore.getState().accessToken
      return fetch(`${API_BASE}/api/conversations/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          conversation_id: conversationId,
          content,
          customer_api_doc: options.customerApiDoc,
          customer_api_doc_type: options.customerApiDocType,
          endpoints_filter_data: options.endpointsFilterData,
          schema_user_import_data: options.schemaUserImportData
        }),
        signal: abortRef.current!.signal
      })
    }

    try {
      let response = await doFetch()

      // On 401 try to refresh the access token via HttpOnly cookie, then retry once
      if (response.status === 401) {
        const refreshed = await tryRefresh()
        if (refreshed) {
          response = await doFetch()
        } else {
          window.dispatchEvent(new CustomEvent('auth:session-expired'))
          setIsLoading(false)
          return
        }
      }

      if (response.status === 429) {
        const retryAfter = response.headers.get('Retry-After')
        let retryMsg = "You've reached the hourly message limit."
        if (retryAfter) {
          const seconds = parseInt(retryAfter, 10)
          if (!isNaN(seconds)) {
            const minutes = Math.ceil(seconds / 60)
            retryMsg += ` Please try again in ${minutes} minute${minutes !== 1 ? 's' : ''}.`
          }
        }
        setMessages(prev => prev.map(m =>
          m.id === assistantId
            ? { ...m, content: retryMsg, isStreaming: false }
            : m
        ))
        setIsLoading(false)
        return
      }

      if (!response.body) throw new Error('No response body')

      const reader = response.body.getReader()
      const decoder = new TextDecoder()
      let buffer = ''
      const toolCallLog: ToolEvent[] = []

      // Reset per-stream state
      streamTextRef.current = ''
      rafPendingRef.current = false

      // Schedule a single RAF to flush buffered text to React state.
      // If a flush is already pending for this frame, do nothing — the
      // already-scheduled callback will pick up the latest ref value.
      const scheduleFlush = () => {
        if (rafPendingRef.current) return
        rafPendingRef.current = true
        rafIdRef.current = requestAnimationFrame(() => {
          rafPendingRef.current = false
          rafIdRef.current = 0
          const text = streamTextRef.current
          setMessages(prev => prev.map(m =>
            m.id === assistantId ? { ...m, content: text } : m
          ))
        })
      }

      // Cancel any pending RAF so stale buffered text can't overwrite a
      // finalised message state (done, error, or user cancel).
      const cancelPendingRaf = () => {
        if (rafPendingRef.current) {
          cancelAnimationFrame(rafIdRef.current)
          rafIdRef.current = 0
          rafPendingRef.current = false
        }
      }

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() || ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6).trim()
          if (!data) continue

          try {
            const event = JSON.parse(data)

            if (event.type === 'text') {
              // Write to ref only — a RAF will flush it to state at most 60×/sec
              streamTextRef.current += event.content
              scheduleFlush()
            }

            else if (event.type === 'tool_call') {
              const toolCall: ToolEvent = {
                type: 'tool_call',
                name: event.name,
                input: event.input
              }
              toolCallLog.push(toolCall)
              setActiveTools(prev => [...prev, toolCall])
            }

            else if (event.type === 'tool_result') {
              // Match to existing tool call and update
              setActiveTools(prev => {
                const updated = [...prev]
                const last = updated.filter(t => t.name === event.name && !t.result).pop()
                if (last) last.result = event.result
                return updated
              })
            }

            else if (event.type === 'config_update') {
              onConfigUpdate?.(event.status)
            }

            else if (event.type === 'done') {
              // Cancel any pending RAF and do one final synchronous flush so
              // the complete text is committed before isStreaming flips false.
              cancelPendingRaf()
              setMessages(prev => prev.map(m =>
                m.id === assistantId
                  ? {
                      ...m,
                      id: event.message_id || m.id,
                      content: streamTextRef.current,
                      isStreaming: false,
                      toolCalls: toolCallLog,
                    }
                  : m
              ))
              setIsLoading(false)
              setActiveTools([])
            }

            else if (event.type === 'error') {
              cancelPendingRaf()
              setMessages(prev => prev.map(m =>
                m.id === assistantId
                  ? { ...m, content: `Error: ${event.message}`, isStreaming: false }
                  : m
              ))
              setIsLoading(false)
            }
          } catch {
            // Skip malformed SSE lines
          }
        }
      }
    } catch (err: unknown) {
      if (err instanceof Error && err.name === 'AbortError') return
      setMessages(prev => prev.map(m =>
        m.id === assistantId
          ? { ...m, content: 'Connection error. Please try again.', isStreaming: false }
          : m
      ))
      setIsLoading(false)
    }
  }, [conversationId, isLoading, onConfigUpdate])

  const cancelStream = useCallback(() => {
    abortRef.current?.abort()
    if (rafPendingRef.current) {
      cancelAnimationFrame(rafIdRef.current)
      rafIdRef.current = 0
      rafPendingRef.current = false
    }
    setIsLoading(false)
    // Flush whatever text was buffered so the message shows up to the cancel point
    setMessages(prev => prev.map(m =>
      m.isStreaming
        ? { ...m, content: streamTextRef.current || m.content, isStreaming: false }
        : m
    ))
  }, [])

  const loadHistory = useCallback((history: ChatMessage[]) => {
    setMessages(history)
  }, [])

  return { messages, isLoading, activeTools, sendMessage, cancelStream, loadHistory }
}
